import BaseModel from "./BaseModel";
import queryString from "query-string";
import Requests from "@/models/Requests";
import { setClarityUser, setGAUser, setHindsightUser } from "@/api/user";

// The session was anonymous until now, so send identity to analytics
// (Clarity identify + tags, GA user_id and Hindsight) right away instead of
// waiting for the next hard page load (plugins/clarity-identify.js).
// `source` is how the visitor was identified: login or signup.
function sendAnalyticsIdentity(data, source) {
  if (!data || !data.id) return;
  const displayName = [data.first_name, data.last_name]
    .filter(Boolean)
    .join(" ");
  setClarityUser({ displayName, email: data.email, uid: data.id });
  setGAUser(data.id, data.email);
  setHindsightUser(data, source);
}

function setAuthSignalCookie(loggedIn) {
  if (typeof document === "undefined") return;
  if (loggedIn) {
    document.cookie = `ln_auth_status=1; path=/; SameSite=Lax; Secure`;
  } else {
    document.cookie = `ln_auth_status=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure`;
  }
}

export default class User extends BaseModel {
  static entity = "profile";

  static findByUrl(params) {
    let url = `/${this.entity}`;
    if (params && Object.keys(params).length > 0) {
      url += "?" + queryString.stringify(params);
    }
    return url;
  }

  static async findOrCreate(params) {
    let { data } = await this.axios.get(this.findByUrl(params));
    await this.create({ data });
    setHindsightUser(data, "profile");
    return this.query().first();
  }

  // authentication methods
  static async login(user) {
    return this.axios.post("/sessions", { user }).then(async ({ data }) => {
      const users = await this.create({ data });
      const user = users.profile[0];
      setAuthSignalCookie(true);
      sendAnalyticsIdentity(data, "login");
      await user.getStripeCustomerSession();
      return user;
    });
  }

  static async logout() {
    setAuthSignalCookie(false);
    await this.axios.delete("/sessions");
    return this.create({ data: {} });
  }
  static async createAccount(user) {
    return this.axios
      .post("/registrations", { user })
      .then(async ({ data }) => {
        const users = await this.create({ data });
        const user = users.profile[0];
        setAuthSignalCookie(true);
        sendAnalyticsIdentity(data, "signup");
        return user;
      });
  }
  static async updateAccount(user) {
    let formData = new FormData();

    Object.keys(user).forEach((k) => {
      formData.append(k, user[k]);
    });

    return this.axios.patch("/profile", formData).then(({ data }) => {
      this.create({ data });
    });
  }

  static async updatePassword(passwordInfo) {
    let formData = new FormData();

    Object.keys(passwordInfo).forEach((k) => {
      formData.append(k, passwordInfo[k]);
    });
    return this.axios.patch("/change_password", formData);
  }

  static async resetPassword(passwordInfo) {
    let formData = new FormData();

    Object.keys(passwordInfo).forEach((k) => {
      formData.append(k, passwordInfo[k]);
    });
    return this.axios.post("/passwords/reset", formData);
  }

  static async forgotPassword(email) {
    return this.axios.post("/passwords/confirmation", {
      email,
    });
  }

  static async getProfile(params = {}) {
    try {
      const browserTimeZone = process.client
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null;
      const query = browserTimeZone ? { ...params, time_zone: browserTimeZone } : params;
      let { data } = await this.axios.get(this.findByUrl(query));
      await this.create({ data });
      setHindsightUser(data, "profile");
      Requests.markAsLoaded("profile");
    } catch (e) {
      console.error(e);
      Requests.markAsErrorOccurred("profile", "failed to load profile");
    }
  }

  static async cancelSubscription() {
    let { data } = await this.axios.delete(`/cancel`);
    await this.create({ data });
    location.reload();
  }

  static get current() {
    return this.query().first();
  }

  static fields() {
    return {
      id: this.attr(null),
      first_name: this.attr(""),
      last_name: this.attr(""),
      email: this.attr(""),
      phone: this.attr(""),
      is_pro: this.attr(false),
      companies: this.attr([]),
      cards: this.attr([]),
      avatar_url: this.attr(null),
      bing_conversion: this.attr(null),
      subscription_access: this.attr(null),
      facebook_conversion: this.attr(null),
      locale_id: this.attr(null),
      checkout_test: this.attr(null),
      documents_in_progress: this.attr(null),
      registered: this.attr(null),
      form_abandon_test: this.attr(null),
      form_test: this.attr(null),
      default_state: this.attr(null),
      is_admin: this.attr(false),
      access_until: this.attr(""),
      apple_pay_eligible: this.attr(false),
      terms_accepted_at: this.attr(null),
      discount_percent_off: this.attr(null),
      stripe_session_client_secret: this.string(null),
      stripe_session_expires_at: this.number(null),
      stripe_id: this.attr(null),
    };
  }

  async createOrUpdateStripeAccount(paymentData) {
    const response = await this.constructor.axios.post(
      "/payment_methods",
      paymentData
    );
    return response;
  }

  async getStripeCustomerSession() {
    if (!this.stripe_id) {
      return null;
    }

    if (this.stripe_session_client_secret && this.stripe_session_expires_at) {
      const now = Math.floor(Date.now() / 1000);
      if (this.stripe_session_expires_at > now) {
        return this.stripe_session_client_secret;
      }
    }

    return await this.setStripeCustomerSession();
  }

  async setStripeCustomerSession() {
    try {
      const { data } = await this.constructor.axios.get(
        "/sessions/stripe_customer_session"
      );

      this.stripe_session_client_secret = data.client_secret;
      this.stripe_session_expires_at = data.expires_at;

      await this.constructor.update({
        where: this.id,
        data: {
          ...this,
          stripe_session_client_secret: data.client_secret,
          stripe_session_expires_at: data.expires_at,
        },
      });

      return data.client_secret;
    } catch (e) {
      console.error("Failed to set Stripe customer session:", e);
      throw e;
    }
  }
}
